import { useState, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../supabase'
import { useBrand } from '../hooks/useBrand'

const NAVY  = '#0B1F3A'
const TEAL  = '#2DD4B0'
const MUTED = '#6B7280'
const BORDER= '#E5E7EB'
const BG    = '#F8FAFC'

function Login() {
  const navigate  = useNavigate()
  const { brand } = useBrand()

  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')
  const [showPass, setShowPass] = useState(false)

  const heroPhoto = useMemo(() => {
    const slots = [brand?.signup_hero_1, brand?.signup_hero_2, brand?.signup_hero_3, brand?.signup_hero_4].filter(Boolean)
    return slots.length ? slots[Math.floor(Math.random() * slots.length)] : null
  }, []) // eslint-disable-line

  const brandName    = brand?.brand_name    || 'PageConnect'
  const brandTagline = brand?.brand_tagline || 'Manage your Facebook shop — smarter'
  const logoUrl      = brand?.logo_light_url || brand?.logo_url || null

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true); setError('')
    const { error: err } = await supabase.auth.signInWithPassword({ email, password })
    if (err) { setError('Invalid email or password. Please try again.'); setLoading(false); return }
    navigate('/dashboard')
  }

  const inp = {
    width: '100%', border: '1.5px solid ' + BORDER, borderRadius: '12px',
    padding: '13px 16px', fontSize: '14px', outline: 'none', background: '#fff',
    color: '#111827', fontFamily: 'Space Grotesk, sans-serif',
    boxSizing: 'border-box', transition: 'border-color 0.15s',
  }

  const STATS = [
    { path: 'M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18', label: 'Active shops',   value: '2,400+' },
    { path: 'M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z', label: 'Orders tracked', value: '180k+' },
    { path: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z M2 12h20', label: 'Countries',      value: '12' },
  ]

  return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <style>{`
        @keyframes floatA { 0%,100%{transform:rotate(-5deg) translateY(0)} 50%{transform:rotate(-5deg) translateY(-6px)} }
        @keyframes floatB { 0%,100%{transform:rotate(-2deg) translateY(0)} 50%{transform:rotate(-2deg) translateY(-4px)} }
        @keyframes floatC { 0%,100%{transform:rotate(0deg) translateY(0)} 50%{transform:rotate(0deg) translateY(-8px)} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes lg-spin { to{transform:rotate(360deg)} }
        .card-a { animation: floatA 5s ease-in-out infinite; }
        .card-b { animation: floatB 5s ease-in-out infinite 0.4s; }
        .card-c { animation: floatC 5s ease-in-out infinite 0.8s; }
        .login-form { animation: fadeIn 0.5s ease both; }
        .login-hero { display: flex !important; }
        @media(max-width:768px) { .login-hero { display: none !important; } }
      `}</style>

      {/* ── LEFT: Hero (60%) ── */}
      <div className="login-hero" style={{ flex: '0 0 60%', background: 'linear-gradient(160deg,' + NAVY + ' 0%,#0D2B4E 60%,#0A2240 100%)', padding: '48px 40px', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', overflow: 'hidden', minHeight: '100vh', display: 'none' }}>

        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '280px', height: '280px', borderRadius: '50%', background: 'rgba(45,212,176,0.06)' }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '220px', height: '220px', borderRadius: '50%', background: 'rgba(45,212,176,0.04)' }} />

        {/* Brand */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          {logoUrl
            ? <img src={logoUrl} alt={brandName} style={{ height: '36px', objectFit: 'contain', marginBottom: '12px', filter: 'brightness(0) invert(1)', display: 'block' }} />
            : <div style={{ fontSize: '22px', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', marginBottom: '8px' }}>{brandName}</div>
          }
          <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, maxWidth: '280px' }}>{brandTagline}</div>
        </div>

        {/* Floating card stack */}
        <div style={{ position: 'relative', zIndex: 2, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '32px 0' }}>
          <div style={{ position: 'relative', width: '240px', height: '300px' }}>

            <div className="card-a" style={{ position: 'absolute', inset: 0, borderRadius: '20px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', transform: 'rotate(-5deg)', backdropFilter: 'blur(4px)' }}>
              <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ height: '8px', width: '60%', borderRadius: '4px', background: 'rgba(255,255,255,0.12)' }} />
                <div style={{ height: '6px', width: '40%', borderRadius: '3px', background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ flex: 1, borderRadius: '10px', background: 'rgba(255,255,255,0.06)', marginTop: '4px' }} />
              </div>
            </div>

            <div className="card-b" style={{ position: 'absolute', inset: 0, borderRadius: '20px', background: 'rgba(255,255,255,0.09)', border: '1px solid rgba(255,255,255,0.16)', transform: 'rotate(-2deg)', backdropFilter: 'blur(4px)' }}>
              <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ height: '8px', width: '70%', borderRadius: '4px', background: 'rgba(45,212,176,0.3)' }} />
                <div style={{ height: '6px', width: '45%', borderRadius: '3px', background: 'rgba(255,255,255,0.10)' }} />
                <div style={{ flex: 1, borderRadius: '10px', background: 'rgba(255,255,255,0.08)', marginTop: '4px' }} />
              </div>
            </div>

            <div className="card-c" style={{ position: 'absolute', inset: 0, borderRadius: '20px', overflow: 'hidden', border: '1.5px solid rgba(45,212,176,0.4)', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
              {heroPhoto ? (
                <img src={heroPhoto} alt="Hero" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', background: 'linear-gradient(160deg,rgba(45,212,176,0.12) 0%,rgba(255,255,255,0.05) 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '24px' }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: 'rgba(45,212,176,0.15)', border: '1px solid rgba(45,212,176,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="1.5" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0"/></svg>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginBottom: '3px' }}>Your shop</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Orders · Products · Customers</div>
                  </div>
                  {[
                    { id: '#1042', status: 'Delivered',  color: '#22C55E' },
                    { id: '#1041', status: 'Shipped',    color: '#F59E0B' },
                    { id: '#1040', status: 'Processing', color: TEAL },
                  ].map(order => (
                    <div key={order.id} style={{ width: '100%', background: 'rgba(255,255,255,0.08)', borderRadius: '8px', padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '11px', color: '#fff', fontWeight: 600 }}>{order.id}</span>
                      <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: order.color + '25', color: order.color }}>{order.status}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '80px', background: 'linear-gradient(to top,rgba(11,31,58,0.7) 0%,transparent 100%)' }} />
              <div style={{ position: 'absolute', bottom: '14px', left: '14px', right: '14px', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', borderRadius: '10px', padding: '7px 12px', border: '1px solid rgba(45,212,176,0.3)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: TEAL, flexShrink: 0, boxShadow: '0 0 6px ' + TEAL }} />
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#fff' }}>2,400+ shops trust {brandName}</span>
              </div>
            </div>

            {/* Floating badges */}
            <div style={{ position: 'absolute', top: '-18px', right: '-18px', background: '#fff', borderRadius: '12px', padding: '8px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '7px', zIndex: 10 }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(45,212,176,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 800, color: NAVY, lineHeight: 1 }}>180k+</div>
                <div style={{ fontSize: '10px', color: MUTED }}>orders tracked</div>
              </div>
            </div>
            <div style={{ position: 'absolute', bottom: '-18px', left: '-18px', background: '#fff', borderRadius: '12px', padding: '8px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '7px', zIndex: 10 }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(45,212,176,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 800, color: NAVY, lineHeight: 1 }}>12 countries</div>
                <div style={{ fontSize: '10px', color: MUTED }}>worldwide</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom stats */}
        <div style={{ position: 'relative', zIndex: 2, display: 'flex', gap: '24px' }}>
          {STATS.map(s => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, opacity: 0.7 }}><path d={s.path}/></svg>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '1px' }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── RIGHT: Form (40%) ── */}
      <div className="login-form" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '48px 40px', background: '#fff', minHeight: '100vh', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: '380px' }}>

          {/* Mobile logo */}
          <div style={{ marginBottom: '32px' }}>
            {logoUrl
              ? <img src={logoUrl} alt={brandName} style={{ height: '32px', objectFit: 'contain', marginBottom: '16px', display: 'block' }} />
              : <div style={{ fontSize: '20px', fontWeight: 800, color: NAVY, marginBottom: '8px' }}>{brandName}</div>
            }
            <div style={{ fontSize: '24px', fontWeight: 800, color: '#111827', letterSpacing: '-0.5px' }}>Welcome back</div>
            <div style={{ fontSize: '13.5px', color: MUTED, marginTop: '4px' }}>Sign in to your seller account</div>
          </div>

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '6px' }}>Email address</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" required autoComplete="email"
                style={inp}
                onFocus={e => e.target.style.borderColor = TEAL}
                onBlur={e => e.target.style.borderColor = BORDER} />
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED }}>Password</label>
                <Link to="/forgot-password" style={{ fontSize: '12px', color: NAVY, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                  Forgot password?
                </Link>
              </div>
              <div style={{ position: 'relative' }}>
                <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required autoComplete="current-password"
                  style={{ ...inp, paddingRight: '52px' }}
                  onFocus={e => e.target.style.borderColor = TEAL}
                  onBlur={e => e.target.style.borderColor = BORDER} />
                <button type="button" onClick={() => setShowPass(s => !s)}
                  style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: MUTED, fontSize: '11.5px', fontWeight: 600, padding: '2px', fontFamily: 'inherit' }}>
                  {showPass ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {error && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '10px 14px', fontSize: '13px', color: '#DC2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
              </div>
            )}

            <button type="submit" disabled={loading}
              style={{ width: '100%', padding: '14px', borderRadius: '12px', background: loading ? BORDER : NAVY, border: 'none', fontSize: '14.5px', fontWeight: 700, color: loading ? '#9CA3AF' : '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'opacity 0.15s' }}>
              {loading && <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'lg-spin 0.7s linear infinite' }} />}
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '13.5px', color: MUTED }}>
            New to {brandName}?{' '}
            <Link to="/signup" style={{ color: NAVY, fontWeight: 700, textDecoration: 'none' }}>Create an account</Link>
          </div>

          <div style={{ marginTop: '32px', textAlign: 'center', fontSize: '11.5px', color: '#9CA3AF' }}>
            By signing in you agree to our{' '}
            <a href="/terms" style={{ color: MUTED, textDecoration: 'none' }}>Terms</a>
            {' · '}
            <a href="/privacy" style={{ color: MUTED, textDecoration: 'none' }}>Privacy</a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login